import groupBy from 'lodash/fp/groupBy';
import type { Dispatch } from 'redux';

import { config } from '../config';
import { fetchData } from '../configuration/setup/fetch';
import type { RootState } from '../configuration/setup/store';
import { getAccessToken } from '../configuration/tokenHandling/tokenSlice';
import { actions } from './actions';
import type { ApiPlatformNotification, ApiPlatformNotificationsList } from './apiTypes';
import { buildFetchResult, Status } from './buildFetchResult';
import { mapPlatformNotifications } from './mapper/mapPlatformNotifications';
import type { PlatformNotification } from './types';

const MAX_ITERATION_PAGE_LIMIT = 100;

/**
 * Note: this is the default and max limit of platform-notifications-api
 * when specified, this limit will be set in the next link too.
 */
const PAGE_ITEMS_LIMIT = 1000;

const paginatedPlatformNotifications = async function* (initialUri: string, token: string) {
    let response: ApiPlatformNotificationsList = { _links: { next: { href: initialUri } }, items: [] };
    let iterations = 0;
    do {
        response = await fetchData(response._links.next.href, token);
        if (!isValid(response)) {
            return;
        }
        yield response.items;
    } while (response.items.length && ++iterations < MAX_ITERATION_PAGE_LIMIT);
};

// Fetch all platform notifications for all assets or for a single one - this is used for polling all states
// but also for on-demand updates of just the platform notifications
export const fetchPlatformNotifications = async (token: string, vehicleId?: string) => {
    const allNotifications: ApiPlatformNotification[] = [];

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('limit', PAGE_ITEMS_LIMIT.toString());

    if (vehicleId) {
        urlSearchParams.set('source_entity_type', 'ASSET');
        urlSearchParams.set('source_entity_identifier', vehicleId);
    }
    const uri = `${config.backend.PLATFORM_NOTIFICATIONS_SERVICE}/notifications?${urlSearchParams}`;

    try {
        for await (const notifications of paginatedPlatformNotifications(uri, token)) {
            notifications && allNotifications.push(...notifications);
        }
        const platformNotifications = mapPlatformNotifications(allNotifications);
        return buildFetchResult(Status.FETCH_SUCCESS, platformNotifications, null);
    } catch (error) {
        return buildFetchResult(Status.FETCH_FAILURE, [] as PlatformNotification[], error as Error);
    }
};

// fetch platform notifications and update the notifications counter
// which comes in handy when a single or all notifications are marked as read or unread
export const fetchPlatformNotificationsUpdates =
    (vehicleId?: string) => async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            const token = getAccessToken(getState());
            const response = await fetchPlatformNotifications(token, vehicleId);
            const platformNotifications = response.data || [];
            const notificationsDictionary = groupBy('assetId', platformNotifications);

            // If a vehicleId is given, but the fetch result is missing that vehicle ID,
            // then the asset has no more notifications, hence we need to clear the counter for that asset
            const clearedAssetIds = vehicleId && !notificationsDictionary[vehicleId] ? [vehicleId] : [];

            dispatch(
                actions.platformNotificationsChanged({
                    notificationsByAsset: notificationsDictionary,
                    clearedAssetIds,
                })
            );
        } catch (error) {
            console.error(error);
        }
    };

const isValid = (apiResponse: Partial<ApiPlatformNotificationsList>): apiResponse is ApiPlatformNotificationsList =>
    apiResponse.items !== undefined && apiResponse?._links?.next?.href !== undefined;
